import React, { useEffect, useState } from 'react';
import type { Account, User } from '@axiom/validation';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '@axiom/ui';

import { UsersApi } from '../../api/users-api';
import { AccountReassignmentNotificationModal } from '../AccountReassignmentNotificationModal/AccountReassignmentNotificationModal';

type ModalData = {
  newUrl: string;
  account: Account;
} | null;

export const AccountReassignmentNotification = ({
  user,
  urlAccountId,
}: {
  user: User;
  urlAccountId?: Account['id'];
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [modalData, setModalData] = useState<ModalData>(null);
  const [{ data: notifications }] = useApi(
    UsersApi.readUserNotifications(user.id)
  );

  const accountIds = notifications.accountReassign.find(
    data => data.oldAccountId === urlAccountId
  );

  const onModalClose = async () => {
    const newAccountId = accountIds?.newAccountId ?? '';
    const newUrl = location.pathname.replace(urlAccountId ?? '', newAccountId);

    if (accountIds?.id) {
      await UsersApi.acknowledgeUserNotifications(user.id, [accountIds.id]);
    }

    setModalData(null);

    void UsersApi.refreshUserNotifications(user.id);

    navigate(newUrl, { replace: true });
  };

  useEffect(() => {
    if (accountIds?.newAccountId && user.contact?.accounts) {
      const account = user.contact.accounts.find(
        acct => acct.id === accountIds.newAccountId
      );

      if (account) {
        setModalData({
          newUrl: location.pathname.replace(
            urlAccountId ?? '',
            accountIds.newAccountId
          ),
          account,
        });
      }
    }

    return () => {
      // Refresh and look for notifications in the event that the location changes
      void UsersApi.refreshUserNotifications(user.id);
    };
  }, [location, accountIds?.newAccountId]);

  return (
    <>
      <Outlet />
      {modalData ? (
        <AccountReassignmentNotificationModal
          name="ACCOUNT_REASSIGNMENT_NOTIFICATION_MODAL"
          data={modalData}
          onClose={onModalClose}
        />
      ) : null}
    </>
  );
};
